import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { FileMetadata } from '../services/storage.js';

interface WorkspaceState {
  storageProvider: string;
  currentPath: string;
  files: FileMetadata[];
  selectedFile: string | null;
  isLoading: boolean;
  error: string | null;
  
  setStorageProvider: (provider: string) => void;
  setCurrentPath: (path: string) => void;
  setFiles: (files: FileMetadata[]) => void;
  setSelectedFile: (file: string | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  
  navigateToPath: (path: string) => void;
  selectFile: (filePath: string) => void;
  clearSelection: () => void;
  
  getCurrentFiles: () => FileMetadata[];
  getFileByPath: (path: string) => FileMetadata | null;
  isFileSelected: (path: string) => boolean;
}

export const useWorkspaceStore = create<WorkspaceState>()(
  persist(
    (set, get) => ({
      storageProvider: 'enterprise',
      currentPath: '/',
      files: [],
      selectedFile: null,
      isLoading: false,
      error: null,
      
      setStorageProvider: (provider: string) => {
        set({ 
          storageProvider: provider,
          currentPath: '/', // Reset to root when changing providers
          files: [],
          selectedFile: null,
          error: null
        });
      },
      
      setCurrentPath: (path: string) => {
        set({ currentPath: path });
      },
      
      setFiles: (files: FileMetadata[]) => {
        set({ files, error: null });
      },
      
      setSelectedFile: (file: string | null) => {
        set({ selectedFile: file });
      },
      
      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },
      
      setError: (error: string | null) => {
        set({ error, isLoading: false });
      },
      
      navigateToPath: (path: string) => {
        set({ 
          currentPath: path,
          selectedFile: null,
          isLoading: true,
          error: null
        });
      },
      
      selectFile: (filePath: string) => {
        const state = get();
        const file = state.getFileByPath(filePath);
        if (file && file.type === 'file') {
          set({ selectedFile: filePath });
        }
      },
      
      clearSelection: () => {
        set({ selectedFile: null });
      },
      
      getCurrentFiles: () => {
        return get().files;
      },
      
      getFileByPath: (path: string) => {
        const findFileRecursive = (files: FileMetadata[], targetPath: string): FileMetadata | null => {
          for (const file of files) {
            if (file.path === targetPath) {
              return file;
            }
            if (file.children) {
              const found = findFileRecursive(file.children, targetPath);
              if (found) return found;
            }
          }
          return null;
        };
        
        return findFileRecursive(get().files, path);
      },
      
      isFileSelected: (path: string) => {
        return get().selectedFile === path;
      },
    }),
    {
      name: 'beehive-workspace-storage',
      partialize: (state) => ({
        storageProvider: state.storageProvider,
        currentPath: state.currentPath,
        selectedFile: state.selectedFile,
      }),
    }
  )
);

export const useStorageProvider = () => useWorkspaceStore((state) => state.storageProvider);
export const useCurrentPath = () => useWorkspaceStore((state) => state.currentPath);
export const useWorkspaceFiles = () => useWorkspaceStore((state) => state.files);
export const useSelectedFile = () => useWorkspaceStore((state) => state.selectedFile);
export const useWorkspaceLoading = () => useWorkspaceStore((state) => state.isLoading);
export const useWorkspaceError = () => useWorkspaceStore((state) => state.error);

export const useWorkspaceActions = () => useWorkspaceStore((state) => ({
  setStorageProvider: state.setStorageProvider,
  setCurrentPath: state.setCurrentPath,
  setFiles: state.setFiles,
  setSelectedFile: state.setSelectedFile,
  setLoading: state.setLoading,
  setError: state.setError,
  navigateToPath: state.navigateToPath,
  selectFile: state.selectFile,
  clearSelection: state.clearSelection,
}));
